import { apiRoutes } from './botApi'
import { getApiClient } from './http'

export const ALLOWED_MIME_TYPES = [
  'text/plain',
  'text/markdown',
  'image/jpeg',
  'image/png',
  'image/gif',
  'text/x-python',
  'text/javascript',
  'text/typescript',
  'text/x-java',
  'text/css',
  'text/html',
  'text/x-c',
  'text/x-c++',
  'application/pdf',
  // 'application/msword',
  // 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
] as const

export const ALLOWED_EXTENSIONS = [
  '.txt', '.md', '.jpg', '.jpeg', '.png', '.gif',
  '.py', '.js', '.ts', '.java', '.css', '.html', '.c', '.cc', '.cpp', '.h',
  '.pdf', //'.doc', '.docx', //These dont work right now
]

export function isAllowedFile(file: File): boolean {
  const typeOk = ALLOWED_MIME_TYPES.includes(file.type as typeof ALLOWED_MIME_TYPES[number])
  const extOk = ALLOWED_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext))
  return typeOk || extOk
}

export interface UploadResult {
  fileId: string
  sessionId: string
}

export interface UploadContext {
  botId: string
  sessionId: string
}

interface PrepareUploadResponse {
  fileId: string
  uploadUrl: string
  uploadHeaders: Record<string, string>
  objectKey: string
  expiresInSeconds: number
}

interface CompleteUploadResponse {
  fileId: string
  name: string
  mimeType: string
  size: number
  status: string
}

async function uploadToPresignedUrl(
  uploadUrl: string,
  file: File,
  uploadHeaders: Record<string, string>,
  onProgress?: (percent: number) => void,
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', uploadUrl)

    Object.entries(uploadHeaders).forEach(([header, value]) => {
      xhr.setRequestHeader(header, value)
    })

    xhr.upload.onprogress = (event) => {
      if (!onProgress || !event.lengthComputable || event.total <= 0) return
      onProgress(Math.round((event.loaded / event.total) * 100))
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100)
        resolve()
        return
      }
      reject(new Error(`S3 upload failed with status ${xhr.status}`))
    }
    xhr.onerror = () => reject(new Error('S3 upload failed'))
    xhr.onabort = () => reject(new Error('S3 upload aborted'))
    xhr.send(file)
  })
}

export async function uploadFile(
  file: File,
  onProgress?: (percent: number) => void,
  context?: UploadContext,
): Promise<UploadResult> {
  if (!context?.botId || !context?.sessionId) {
    throw new Error('A bot-scoped session is required before uploading files')
  }
  const prepared = await getApiClient().post<PrepareUploadResponse>(
    apiRoutes.files.prepareUpload.path(context.botId, context.sessionId),
    {
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
      size: file.size,
    },
  )

  await uploadToPresignedUrl(prepared.data.uploadUrl, file, prepared.data.uploadHeaders, onProgress)

  const completed = await getApiClient().post<CompleteUploadResponse>(
    apiRoutes.files.completeUpload.path(context.botId, context.sessionId, prepared.data.fileId),
  )
  return {
    fileId: completed.data.fileId,
    sessionId: context.sessionId,
  }
}

export async function deleteFile(fileId: string, context: UploadContext, _baseUrl = ''): Promise<void> {
  await getApiClient().delete(apiRoutes.files.delete.path(context.botId, context.sessionId, fileId))
}
